import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import { Box, Chip, Fade, Grid, IconButton, Tooltip } from '@mui/material';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import { includes, indexOf, size } from 'lodash';
import React, { useEffect, useMemo } from 'react';
import { StringParam, useQueryParam } from 'use-query-params';

import DashboardTabs from 'components/DashboardTabs';
import useMainFilters from 'components/SearchFilters/hooks/useMainFilters';
import { reportTabJobStatuses } from 'components/StudyDashboard/Tabs/reportTabJobs';
import { JobArchiveState, JobType } from 'interfaces/job';
import { Permission } from 'interfaces/permissionOption';
import { MULTIPLEX_STAIN_ID } from 'interfaces/stainType';
import { useJobs } from 'utils/queryHooks/jobs/useJobs';
import { useStudyStainTypeIds } from 'utils/queryHooks/stainType/useStudyStainTypes';
import { usePermissions } from 'utils/usePermissions';
import useStudy from 'utils/useStudy';
import {
  DeferredStudyDashboardTabRequirementState,
  getDeferredStudyDashboardTabDecision,
  getRnaOnlyStudySetupRequirementState,
  getStudyDashboardTabs,
  getStudyFitDeferredStudyDashboardTabDecision,
  getStudyFitOrDataDeferredStudyDashboardTabDecision,
  shouldShowRnaIncompatibleStudyDashboardTabs,
  shouldShowStudySpecificMultiplexTabs,
} from './getStudyDashboardTabs';
import { proceduresContentHorizontalPaddingX } from './studyDashboardSpacing';
import {
  CellNeighborhoodsTab,
  DatasetTab,
  MifMarkerUnsupervisedAnalysisTab,
  QaAnalysisTab,
  QCMarkersTab,
  StudyDashboardTabOptions,
  StudyReportTab,
  useStudyDashboardTabState,
  WorkflowTab,
} from './useStudyDashboardTabState';

const StudyDashboardTabs: React.FunctionComponent<
  React.PropsWithChildren<{
    studyId: string;
  }>
> = ({ studyId }) => {
  const queryClient = useQueryClient();
  const { hasPermission, isLoading: isLoadingPermissions } = usePermissions();
  const { isStudyIdSpecific } = useMainFilters();
  const specificStudySelected = isStudyIdSpecific(studyId);

  const { data: stainTypesInStudy, isLoading: isLoadingStudyStainTypes } = useStudyStainTypeIds(studyId, {
    enabled: specificStudySelected,
  });
  const { study, isLoading: isLoadingStudy } = useStudy(studyId, { enabled: specificStudySelected });

  const isMultiplex = includes(stainTypesInStudy, MULTIPLEX_STAIN_ID);
  const canShowStudySpecificMultiplexTabs = shouldShowStudySpecificMultiplexTabs({
    specificStudySelected,
    isMultiplex,
  });
  const rnaOnlyStudySetupRequirementState = getRnaOnlyStudySetupRequirementState({
    isLoadingStudy,
    specificStudySelected,
    workflowModules: study?.settings?.workflow?.modules,
  });
  const canShowRnaIncompatibleStudyDashboardTabs = shouldShowRnaIncompatibleStudyDashboardTabs(
    rnaOnlyStudySetupRequirementState
  );
  const rnaOnlyStudySetupRequirement = {
    description: 'RNA-only study setup',
    kind: 'study-fit' as const,
    state: rnaOnlyStudySetupRequirementState,
  };

  const canViewAnnotationAssignmentsDashboard = hasPermission(Permission.ViewAnnotationsList);
  const canViewJobs = hasPermission(Permission.ViewJobs);
  const hasPermissionToEditCellRules = hasPermission(Permission.EditCellRules);
  const hasPermissionToViewStudyCellRules = hasPermission(Permission.ViewStudyCellRules);
  const hasPermissionToViewQCMarkers = hasPermission(Permission.ViewQCMarkers);
  const hasPermissionToEditQCMarkers = hasPermission(Permission.EditQCMarkers);
  const [activeTab, setActiveTab] = useStudyDashboardTabState();
  const [requestedStudyReportJobId] = useQueryParam('jobIdForStudyReportReportTab', StringParam);

  const studyFitRequirementState: DeferredStudyDashboardTabRequirementState =
    specificStudySelected && isLoadingStudyStainTypes
      ? 'pending'
      : canShowStudySpecificMultiplexTabs
      ? 'available'
      : 'unavailable';

  // Cell Rules and Markers QC are study-specific multiplex tabs. Keep them hidden when the
  // dashboard does not have a specific study selection, even if an "All studies" filter is active.
  const canViewCellRulesTab =
    canShowStudySpecificMultiplexTabs &&
    canShowRnaIncompatibleStudyDashboardTabs &&
    (hasPermissionToViewStudyCellRules || hasPermissionToEditCellRules);
  const canViewQCMarkersTab =
    canShowStudySpecificMultiplexTabs && (hasPermissionToViewQCMarkers || hasPermissionToEditQCMarkers);

  const workflowMonitorPermissionRequirementState: DeferredStudyDashboardTabRequirementState = isLoadingPermissions
    ? 'pending'
    : hasPermission(Permission.ViewWorkflowMonitor)
    ? 'available'
    : 'unavailable';
  const workflowStudyRequirementState: DeferredStudyDashboardTabRequirementState = specificStudySelected
    ? 'available'
    : 'unavailable';
  const workflowTabDecision = useMemo(
    () =>
      getDeferredStudyDashboardTabDecision({
        activeTab,
        tab: WorkflowTab,
        requirements: [
          {
            description: 'workflow monitor permission',
            kind: 'permission',
            state: workflowMonitorPermissionRequirementState,
          },
          {
            description: 'specific study selected',
            kind: 'study-fit',
            state: workflowStudyRequirementState,
          },
        ],
      }),
    [activeTab, workflowStudyRequirementState, workflowMonitorPermissionRequirementState]
  );

  const qaAnalysisPermissionRequirementState: DeferredStudyDashboardTabRequirementState = isLoadingPermissions
    ? 'pending'
    : hasPermission(Permission.ViewQaAnalysisResults)
    ? 'available'
    : 'unavailable';
  const qaAnalysisTabDecision = useMemo(
    () =>
      getStudyFitDeferredStudyDashboardTabDecision({
        activeTab,
        permissionRequirement: {
          description: 'QA Analysis permission',
          kind: 'permission',
          state: qaAnalysisPermissionRequirementState,
        },
        studyFitRequirement: {
          description: 'mIF study fit',
          kind: 'study-fit',
          state: studyFitRequirementState,
        },
        additionalRequirements: [rnaOnlyStudySetupRequirement],
        tab: QaAnalysisTab,
      }),
    [activeTab, qaAnalysisPermissionRequirementState, rnaOnlyStudySetupRequirement, studyFitRequirementState]
  );

  const mifMarkerUnsupervisedAnalysisPermissionRequirementState: DeferredStudyDashboardTabRequirementState =
    isLoadingPermissions
      ? 'pending'
      : hasPermission(Permission.ViewMifMarkerUnsupervisedAnalysisResults)
      ? 'available'
      : 'unavailable';
  const mifMarkerUnsupervisedAnalysisTabDecision = useMemo(
    () =>
      getStudyFitDeferredStudyDashboardTabDecision({
        activeTab,
        permissionRequirement: {
          description: 'mIF unsupervised analysis permission',
          kind: 'permission',
          state: mifMarkerUnsupervisedAnalysisPermissionRequirementState,
        },
        studyFitRequirement: {
          description: 'mIF study fit',
          kind: 'study-fit',
          state: studyFitRequirementState,
        },
        additionalRequirements: [rnaOnlyStudySetupRequirement],
        tab: MifMarkerUnsupervisedAnalysisTab,
      }),
    [
      activeTab,
      mifMarkerUnsupervisedAnalysisPermissionRequirementState,
      rnaOnlyStudySetupRequirement,
      studyFitRequirementState,
    ]
  );

  const studyReportPermissionRequirementState: DeferredStudyDashboardTabRequirementState = isLoadingPermissions
    ? 'pending'
    : hasPermission(Permission.ViewStudyReportResults)
    ? 'available'
    : 'unavailable';
  const canQueryStudyReportJobs = studyReportPermissionRequirementState === 'available';
  const { data: studyReportJobsResponse, isLoading: isLoadingStudyReportJobs } = useJobs({
    page: 1,
    pageSize: 1,
    enabled: canQueryStudyReportJobs && Boolean(studyId),
    noInitialData: true,
    keepPreviousData: false,
    additionalFilters: {
      archiveState: JobArchiveState.Active,
      types: [JobType.StudyReport],
      statuses: [...reportTabJobStatuses],
      studyId,
    },
  });
  const hasStudyReportJobs = Boolean(requestedStudyReportJobId) || (studyReportJobsResponse?.totalJobs ?? 0) > 0;
  const studyReportDataRequirementState: DeferredStudyDashboardTabRequirementState = canQueryStudyReportJobs
    ? isLoadingStudyReportJobs
      ? 'pending'
      : hasStudyReportJobs
      ? 'available'
      : 'unavailable'
    : studyReportPermissionRequirementState === 'pending'
    ? 'pending'
    : 'unavailable';
  const studyReportTabDecision = useMemo(
    () =>
      getDeferredStudyDashboardTabDecision({
        activeTab,
        tab: StudyReportTab,
        // Study Report is intentionally not gated by mIF study-fit so brightfield studies
        // with study-report jobs can show the tab on the dashboard as well.
        requirements: [
          {
            description: 'Study Report permission',
            kind: 'permission',
            state: studyReportPermissionRequirementState,
          },
          {
            description: 'Study Report jobs',
            kind: 'data',
            state: studyReportDataRequirementState,
          },
        ],
      }),
    [activeTab, studyReportDataRequirementState, studyReportPermissionRequirementState]
  );

  const cellNeighborhoodsPermissionRequirementState: DeferredStudyDashboardTabRequirementState = isLoadingPermissions
    ? 'pending'
    : hasPermission(Permission.ViewJobs)
    ? 'available'
    : 'unavailable';
  const canQueryCellNeighborhoodJobs =
    cellNeighborhoodsPermissionRequirementState === 'available' && studyFitRequirementState === 'unavailable';
  const { data: cellNeighborhoodJobsResponse, isLoading: isLoadingCellNeighborhoodJobs } = useJobs({
    page: 1,
    pageSize: 1,
    enabled: canQueryCellNeighborhoodJobs && Boolean(studyId),
    noInitialData: true,
    keepPreviousData: false,
    additionalFilters: {
      types: [JobType.MultiplexNeighborhoodClusters, JobType.NeighborhoodClusters],
      statuses: [...reportTabJobStatuses],
      studyId,
    },
  });
  const hasCellNeighborhoodJobs = (cellNeighborhoodJobsResponse?.totalJobs ?? 0) > 0;
  const cellNeighborhoodsDataRequirementState: DeferredStudyDashboardTabRequirementState = canQueryCellNeighborhoodJobs
    ? isLoadingCellNeighborhoodJobs
      ? 'pending'
      : hasCellNeighborhoodJobs
      ? 'available'
      : 'unavailable'
    : cellNeighborhoodsPermissionRequirementState === 'pending'
    ? 'pending'
    : 'unavailable';
  const cellNeighborhoodsTabDecision = useMemo(
    () =>
      getStudyFitOrDataDeferredStudyDashboardTabDecision({
        activeTab,
        dataRequirement: {
          description: 'Cell Neighborhoods jobs',
          kind: 'data',
          state: cellNeighborhoodsDataRequirementState,
        },
        permissionRequirement: {
          description: 'Jobs permission',
          kind: 'permission',
          state: cellNeighborhoodsPermissionRequirementState,
        },
        studyFitRequirement: {
          description: 'mIF study fit',
          kind: 'study-fit',
          state: studyFitRequirementState,
        },
        additionalRequirements: [rnaOnlyStudySetupRequirement],
        tab: CellNeighborhoodsTab,
      }),
    [
      activeTab,
      cellNeighborhoodsDataRequirementState,
      cellNeighborhoodsPermissionRequirementState,
      rnaOnlyStudySetupRequirement,
      studyFitRequirementState,
    ]
  );

  const tabs: StudyDashboardTabOptions[] = useMemo(() => {
    return getStudyDashboardTabs({
      canViewAnnotationAssignmentsDashboard,
      canViewJobs,
      canViewCellRulesTab,
      canViewQCMarkersTab,
      workflowTabDecision,
      deferredTabDecisions: [
        qaAnalysisTabDecision,
        mifMarkerUnsupervisedAnalysisTabDecision,
        cellNeighborhoodsTabDecision,
        studyReportTabDecision,
      ],
    });
  }, [
    canViewAnnotationAssignmentsDashboard,
    canViewJobs,
    canViewCellRulesTab,
    canViewQCMarkersTab,
    workflowTabDecision,
    qaAnalysisTabDecision,
    mifMarkerUnsupervisedAnalysisTabDecision,
    cellNeighborhoodsTabDecision,
    studyReportTabDecision,
  ]);

  const tabsDisplayTexts = useMemo(
    () =>
      tabs.map((tab) => {
        if (!includes([WorkflowTab, QCMarkersTab], tab)) {
          return <React.Fragment key={tab}>{tab}</React.Fragment>;
        }

        return (
          <React.Fragment key={tab}>
            <Box component="span" sx={{ alignItems: 'center', display: 'inline-flex', gap: 0.75 }}>
              <Box component="span">{tab}</Box>
              <Chip
                color="warning"
                component="span"
                label="Beta"
                size="small"
                sx={{
                  height: 20,
                  '& .MuiChip-label': {
                    fontSize: '0.6875rem',
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    px: 1,
                    textTransform: 'uppercase',
                  },
                }}
              />
            </Box>
          </React.Fragment>
        );
      }),
    [tabs]
  );

  const tabAriaLabels = useMemo(
    () => tabs.map((tab) => (includes([WorkflowTab, QCMarkersTab], tab) ? `${tab} Beta` : tab)),
    [tabs]
  );

  const hasAnyOtherTabPermission = size(tabs) > 1;

  useEffect(() => {
    // Cell Rules/Markers QC visibility depends on the study-fit check (stain types query), which
    // can still be in flight after permissions resolve. Wait for it too, or a slow-loading study
    // momentarily drops the active tab from `tabs` and bounces the user back to Dataset.
    if (!isLoadingPermissions && studyFitRequirementState !== 'pending') {
      if (!includes(tabs, activeTab as string)) {
        setActiveTab(DatasetTab);
      }
    }
  }, [tabs, activeTab, setActiveTab, isLoadingPermissions, studyFitRequirementState]);

  const activeTabIndex = indexOf(tabs, activeTab as string) !== -1 ? indexOf(tabs, activeTab as string) : 0;
  const changeTab = (newTab: StudyDashboardTabOptions) => {
    setActiveTab(newTab);
  };
  const activeTabLabel = includes(tabs, activeTab as string) ? activeTab : 'dashboard';
  const isFetching = useIsFetching();
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const handleRefreshTabData = () => {
    setIsRefreshing(true);
    void queryClient.refetchQueries({ type: 'active' });
  };

  React.useEffect(() => {
    if (isRefreshing && isFetching === 0) {
      setIsRefreshing(false);
    }
  }, [isFetching, isRefreshing]);

  return (
    <Fade in={hasAnyOtherTabPermission} timeout={400} unmountOnExit>
      <Grid
        item
        sx={{
          px: proceduresContentHorizontalPaddingX,
          width: '100%',
          '@keyframes tabSlideIn': {
            from: { opacity: 0, transform: 'translateY(4px)' },
            to: { opacity: 1, transform: 'translateY(0)' },
          },
          '& .MuiTab-root': {
            animation: 'tabSlideIn 300ms ease-out both',
          },
          ...Object.fromEntries(
            tabs.map((_, i) => [`& .MuiTab-root:nth-of-type(${i + 1})`, { animationDelay: `${i * 60}ms` }])
          ),
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <DashboardTabs
              tabsDisplayTexts={tabsDisplayTexts}
              tabs={tabs}
              tabAriaLabels={tabAriaLabels}
              changeTab={changeTab}
              activeTabIndex={activeTabIndex}
            />
          </Box>
          <Tooltip title={`Refresh ${activeTabLabel} data`}>
            <IconButton
              aria-label={`Refresh ${activeTabLabel} data`}
              onClick={handleRefreshTabData}
              disabled={isRefreshing}
              size="small"
              sx={{
                flexShrink: 0,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1.5,
                color: 'text.secondary',
                '&:hover': {
                  color: 'text.primary',
                },
              }}
            >
              <RefreshRoundedIcon
                fontSize="small"
                sx={{
                  ...(isRefreshing && {
                    animation: 'spin 1s linear infinite',
                    '@keyframes spin': {
                      from: { transform: 'rotate(0deg)' },
                      to: { transform: 'rotate(360deg)' },
                    },
                  }),
                }}
              />
            </IconButton>
          </Tooltip>
        </Box>
      </Grid>
    </Fade>
  );
};

export default StudyDashboardTabs;
